import {
  Accordion,
  AccordionItem,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { AccountType } from 'api/accountType';
import { useUserContext } from 'context/UserContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import useRetireLegacyVagAccountsMutation from 'hooks/mutations/useRetireLegacyVagAccountsMutation';
import useUpdateAccountStatus from 'hooks/mutations/useUpdateAccountStatus';
import useAccountDetailQuery from 'hooks/useAccountDetailQuery';
import usePortalsQuery from 'hooks/usePortalsQuery';
import useStatusQuery from 'hooks/useStatusQuery';
import useWorkshopAccountsQuery from 'hooks/useWorkshopAccountsQuery';
import { forEach, get, isNil } from 'lodash';
import AddAccountModal from 'pages/workshop/AddAccountModal';
import SetLegalFormModal from 'pages/workshop/SetLegalFormModal';
import { useMemo, useState } from 'react';
import { getI18n, Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { dropdown } from '../helpers/makeFormFields';
import { LoadingSpinner } from './LoadingSpinner';
import FormEmbeddedAlertModal from './modals/FormEmbeddedAlertModal';
import { StatusTag } from './StatusTag';
import { DropdownOption } from './Table/Filter';

const schema = yup
  .object({
    status: yup.number().nullable().required(),
  })
  .required();

const VAG_DSS_PORTAL_SLUG = 'vag_dss';

export const AccountStatusCard = ({
  account,
  isLoading,
  sendRequest,
  openTaskAssignedToUser,
}: {
  account: any;
  isLoading: boolean;
  sendRequest(x: object): Promise<void>;
  openTaskAssignedToUser: boolean;
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { statusTransitions } = useUserContext();
  const statusQuery = useStatusQuery();
  const portalsQuery = usePortalsQuery();
  const toast = useToast();
  const updateAccountStatus = useUpdateAccountStatus(account?.id);
  const retireLegacyVagAccountsMutation = useRetireLegacyVagAccountsMutation();
  const transitions = get(statusTransitions?.account, account?.status, []);
  const hasNoUserNameOrPassword = !account?.username || !account?.password;
  const missingRequiredTotp = !!account?.portal?.requires_totp && !account?.has_totp_secret;
  const canMarkAsRegistered = !hasNoUserNameOrPassword && !missingRequiredTotp;
  const accountStatusOnHoldAndNoOpenTask = account.status === 150 && isNil(account?.open_task);
  const portalRegistrationNeeded = account?.portal?.external_registration === 1;
  const accountStatusLessThenRegistered = account?.status < 200 && account?.status !== 150;
  const isLegacyVagPortal = !!account?.is_legacy_vag_account;
  const isVagDssPortal = account?.portal?.slug === VAG_DSS_PORTAL_SLUG;
  const needsLegalForm = isVagDssPortal && !account?.workshop?.legal_form;
  const canConvertToSubAccount = !!account?.can_convert_to_sub_account && !!account?.open_task && account?.type === AccountType.WP;
  const enum AccountStatus {
    REGISTERED = 200,
    REJECTED = 800,
    BLOCKED = 700,
    DELETED = 900,
    REGISTRATION_PENDING = 100,
    NOT_REGISTERED = 0,
  }

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenReRegistration, onOpen: onOpenReRegistration, onClose: onCloseReRegistration } = useDisclosure();
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
  const { isOpen: isOpenCreateVagAccount, onOpen: onOpenCreateVagAccount, onClose: onCloseCreateVagAccount } = useDisclosure();
  const { isOpen: isOpenRetireLegacyChoice, onOpen: onOpenRetireLegacyChoice, onClose: onCloseRetireLegacyChoice } = useDisclosure();
  const { isOpen: isOpenConvertToSubAccount, onOpen: onOpenConvertToSubAccount, onClose: onCloseConvertToSubAccount } = useDisclosure();
  const { isOpen: isOpenSetLegalForm, onOpen: onOpenSetLegalForm, onClose: onCloseSetLegalForm } = useDisclosure();
  const [pendingRetirementAccountId, setPendingRetirementAccountId] = useState<number | null>(null);

  const shadow = useColorModeValue('md', 'md-dark');

  const markAsRegistered = () => {
    updateAccountStatus.mutate({
      account_id: account?.id,
      status: 200,
      portal_id: account?.portal?.id,
      workshop_id: account?.workshop_id,
      username: account?.username,
      password: account?.password,
      random_password: false,
    });
  };

  const handleAccountStatusChange = (status: AccountStatus, onCloseCallback: () => void) => {
    updateAccountStatus.mutate(
      {
        account_id: account?.id,
        status,
        portal_id: account?.portal?.id,
        workshop_id: account?.workshop_id,
      },
      {
        onError: (error: any) => {
          onCloseCallback();
          manageErrorResponse(toast, error.response);
        },
        onSuccess: () => {
          onCloseCallback();
        },
      },
    );
  };

  const vagDssPortal = portalsQuery.data?.find((portal) => portal.slug === VAG_DSS_PORTAL_SLUG);

  const vagAccountInitialValues = useMemo(
    () => ({
      portal_id: vagDssPortal?.id,
      type: AccountType.WP,
      status: 'not_registered' as const,
      create_task: true,
      username: account?.username ?? '',
    }),
    [vagDssPortal?.id, account?.username],
  );

  const vagWorkshopAccountsQuery = useWorkshopAccountsQuery(
    String(account?.workshop_id),
    { workshopId: String(account?.workshop_id), portalId: vagDssPortal?.id },
    isLegacyVagPortal && !!vagDssPortal?.id,
  );
  const existingVagDssAccount = vagWorkshopAccountsQuery.data?.data?.[0];

  const handleReRegistrationClick = () => {
    if (!isLegacyVagPortal) {
      onOpenReRegistration();
      return;
    }

    if (existingVagDssAccount) {
      setPendingRetirementAccountId(Number(existingVagDssAccount.id));
      onOpenRetireLegacyChoice();
      return;
    }

    onOpenCreateVagAccount();
  };

  const handleRetireLegacyChoice = (createTask: boolean) => {
    if (pendingRetirementAccountId == null) {
      return;
    }

    const vagDssAccountId = pendingRetirementAccountId;

    retireLegacyVagAccountsMutation.mutate(
      { vagDssAccountId, createTask },
      {
        onError: (error: any) => manageErrorResponse(toast, error.response),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: useAccountDetailQuery.getKey(vagDssAccountId) });
          queryClient.invalidateQueries({ queryKey: ['WorkshopAccountsQuery'] });
          navigate(`/app/op/account/${vagDssAccountId}`);
        },
      },
    );

    onCloseRetireLegacyChoice();
    setPendingRetirementAccountId(null);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ marginBottom: -2 }}>
      <Stack
        direction={'column'}
        w={'100%'}
        p={6}
        borderRadius="lg"
        boxShadow={shadow}
        pos="relative"
        data-test-id="status-card"
        bgColor={account?.status === 200 ? 'green.200' : account?.status === 700 || account?.status === 900 ? 'red.200' : 'orange.200'}
      >
        <Accordion allowToggle defaultIndex={[0]}>
          <AccordionItem border="none">
            <HStack justifyContent="space-between">
              <StatusTag resourceKey={'account'} currentStatus={account?.status ?? 0} currentStatusReason={account?.status_reason} />
              {openTaskAssignedToUser && (
                <Button
                  onClick={() => {
                    NiceModal.show(FormEmbeddedAlertModal as any, {
                      content: {
                        header: t('components:update_status_modal.header'),
                        formTestId: 'status-form',
                        formFields: (props: { control: any; errors: any; watchedFields: { status: number; status_reason: number } }) => {
                          const getTransitions = (statusCodes: any, resourceKey: string, transitions: any) => {
                            watchedFields.status;
                            const transitionOptions: DropdownOption[] = [
                              {
                                label: getI18n().t('components:update_status_modal.transitions.choose'),
                                value: 'unknown',
                              },
                            ];
                            forEach(transitions, (transition) => {
                              transitionOptions.push({
                                label: getI18n().t(
                                  resourceKey +
                                    ':status.' +
                                    (statusCodes && statusCodes[transition] ? statusCodes[transition] + '.label' : 'unknown'),
                                ),
                                value: transition,
                              });
                            });
                            return transitionOptions;
                          };

                          const getReasons = (statusCodes: any, reasons: any, resourceKey: string, selectedStatus: number) => {
                            const currentReasons = reasons || {};

                            const reasonOptions = [
                              {
                                label: getI18n().t('components:update_status_modal.reasons.choose'),
                                value: 'unknown',
                              },
                            ];

                            if (selectedStatus) {
                              forEach(Object.keys(currentReasons), (key) => {
                                reasonOptions.push({
                                  label: getI18n().t(
                                    `${resourceKey}:status.${statusCodes}.${
                                      currentReasons && currentReasons[key] ? currentReasons[key] + '.label' : 'unknown'
                                    }`,
                                  ),
                                  value: key,
                                });
                              });
                            }

                            return reasonOptions;
                          };
                          const { control, errors, watchedFields } = props;
                          const selectedStatus = watchedFields.status;
                          const reasonsOptions = getReasons(
                            statusQuery.data?.account?.[selectedStatus],
                            statusQuery.data?.account_reasons?.[selectedStatus],
                            'account',
                            selectedStatus,
                          );

                          return (
                            <Box>
                              <Stack spacing={2}>
                                <VStack alignItems={'flex-start'}>
                                  <>
                                    {dropdown({
                                      schema,
                                      name: 'status',
                                      label: t('forms:status.label'),
                                      //@ts-ignore
                                      control,
                                      errors,
                                      options: getTransitions(statusQuery.data?.account, 'account', transitions),
                                    })}
                                    {reasonsOptions.length > 1 &&
                                      dropdown({
                                        name: 'status_reason',
                                        label: t('components:update_status_modal.status_reason.label'),
                                        // @ts-ignore
                                        control,
                                        errors,
                                        options: reasonsOptions,
                                      })}
                                  </>
                                </VStack>
                              </Stack>
                            </Box>
                          );
                        },
                        footer: {
                          buttons: {
                            formButtonTestId: 'scan-submit-button',
                            actionCaption: t('common:edit'),
                          },
                        },
                      },
                      onMutate: (formData: any) => {
                        if (needsLegalForm && Number(formData?.status) === AccountStatus.REGISTERED) {
                          onOpenSetLegalForm();
                          return Promise.resolve();
                        }
                        return sendRequest(formData);
                      },
                      schema,
                      defaultValues: {
                        status: null,
                        status_reason: 0,
                        //@ts-ignore
                        portal_id: account?.portal?.id,
                        workshop_id: account?.workshop_id,
                        username: account?.username,
                        password: account?.password,
                        random_password: false,
                      },
                    });
                  }}
                  variant="primary"
                  size="sm"
                  bg={'blue.500'}
                  _hover={{
                    bg: 'blue.600',
                  }}
                  data-test-id="credentials-submit-button"
                >
                  {t('common:edit')}
                </Button>
              )}
            </HStack>
          </AccordionItem>
        </Accordion>
      </Stack>
      <HStack mt="4">
        {account?.status !== AccountStatus.BLOCKED &&
          account?.status !== AccountStatus.DELETED &&
          account?.status !== AccountStatus.REGISTRATION_PENDING && (
            <>
              <Button
                color="blue.500"
                borderColor="blue.500"
                variant="outline"
                _hover={{
                  bg: 'blue.500',
                  color: 'white',
                }}
                _active={{
                  bg: 'blue.500',
                  color: 'white',
                }}
                onClick={onOpen}
              >
                {t('account:status.marked_as_buttons.blocked.marked_as_blocked')}
              </Button>
              <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>{t('account:status.marked_as_buttons.blocked.marked_as_blocked')}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>{t('account:status.marked_as_buttons.blocked.modal_description')}</ModalBody>
                  <ModalFooter>
                    <Button variant="secondary" mr={3} onClick={onClose}>
                      {t('account:status.retry_registration.dialog.button.cancel')}
                    </Button>
                    <Button variant="primary" onClick={() => handleAccountStatusChange(AccountStatus.BLOCKED, onClose)}>
                      {t('account:status.marked_as_buttons.blocked.marked_as_blocked')}
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </>
          )}

        {(account?.status === AccountStatus.REGISTERED || account?.status === AccountStatus.BLOCKED || account?.status === AccountStatus.REJECTED) &&
          account?.status !== AccountStatus.REGISTRATION_PENDING && (
            <>
              <Button
                color="blue.500"
                borderColor="blue.500"
                variant="outline"
                _hover={{
                  bg: 'blue.500',
                  color: 'white',
                }}
                _active={{
                  bg: 'blue.500',
                  color: 'white',
                }}
                onClick={handleReRegistrationClick}
              >
                {t('account:status.marked_as_buttons.re-registration.marked_as_requires_re_registration')}
              </Button>
              <Modal isOpen={isOpenReRegistration} onClose={onCloseReRegistration} size="lg">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>{t('account:status.marked_as_buttons.re-registration.marked_as_requires_re_registration')}</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    <Trans i18nKey="account:status.marked_as_buttons.re-registration.modal_description" />
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="secondary" mr={3} onClick={onCloseReRegistration}>
                      {t('account:status.retry_registration.dialog.button.cancel')}
                    </Button>
                    <Button variant="primary" onClick={() => handleAccountStatusChange(AccountStatus.NOT_REGISTERED, onCloseReRegistration)}>
                      {t('account:status.marked_as_buttons.re-registration.confirm')}
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
              {isLegacyVagPortal && (
                <>
                  <AddAccountModal
                    workshopId={account?.workshop_id}
                    workshopCountry={account?.workshop?.country}
                    isOpen={isOpenCreateVagAccount}
                    onClose={onCloseCreateVagAccount}
                    initialValues={vagAccountInitialValues}
                    onSuccess={(accountId) => {
                      setPendingRetirementAccountId(accountId);
                      onOpenRetireLegacyChoice();
                    }}
                  />
                  <Modal isOpen={isOpenRetireLegacyChoice} onClose={onCloseRetireLegacyChoice} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                      <ModalHeader>{t('account:status.marked_as_buttons.re-registration.retire_legacy_choice.header')}</ModalHeader>
                      <ModalCloseButton />
                      <ModalBody>
                        <Trans i18nKey="account:status.marked_as_buttons.re-registration.retire_legacy_choice.description" />
                      </ModalBody>
                      <ModalFooter>
                        <Button variant="secondary" mr={3} onClick={onCloseRetireLegacyChoice}>
                          {t('account:status.retry_registration.dialog.button.cancel')}
                        </Button>
                        <Button variant="outline" mr={3} onClick={() => handleRetireLegacyChoice(false)}>
                          {t('account:status.marked_as_buttons.re-registration.retire_legacy_choice.not_registered')}
                        </Button>
                        <Button colorScheme="red" onClick={() => handleRetireLegacyChoice(true)}>
                          {t('account:status.marked_as_buttons.re-registration.retire_legacy_choice.create_task')}
                        </Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>
                </>
              )}
            </>
          )}

        {account?.status !== AccountStatus.DELETED && account?.status !== AccountStatus.REGISTRATION_PENDING && (
          <>
            <Button
              color="red.500"
              borderColor="red.500"
              variant="outline"
              _hover={{
                bg: 'red.500',
                color: 'white',
              }}
              _active={{
                bg: 'red.500',
                color: 'white',
              }}
              onClick={onOpenDelete}
            >
              {t('account:status.marked_as_buttons.deleted.marked_as_deleted')}
            </Button>
            <Modal isOpen={isOpenDelete} onClose={onCloseDelete} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>{t('account:status.marked_as_buttons.deleted.marked_as_deleted')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Trans i18nKey="account:status.marked_as_buttons.deleted.modal_description" />
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondary" mr={3} onClick={onCloseDelete}>
                    {t('account:status.retry_registration.dialog.button.cancel')}
                  </Button>
                  <Button colorScheme="red" onClick={() => handleAccountStatusChange(AccountStatus.DELETED, onCloseDelete)}>
                    {t('account:status.marked_as_buttons.deleted.marked_as_deleted')}
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </>
        )}

        {canConvertToSubAccount && (
          <>
            <Button
              color="orange.500"
              borderColor="orange.500"
              variant="outline"
              _hover={{
                bg: 'orange.500',
                color: 'white',
              }}
              _active={{
                bg: 'orange.500',
                color: 'white',
              }}
              onClick={onOpenConvertToSubAccount}
            >
              {t('account:status.marked_as_buttons.convert_to_sub_account.label')}
            </Button>
            <Modal isOpen={isOpenConvertToSubAccount} onClose={onCloseConvertToSubAccount} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>{t('account:status.marked_as_buttons.convert_to_sub_account.label')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Trans i18nKey="account:status.marked_as_buttons.convert_to_sub_account.modal_description" />
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondary" mr={3} onClick={onCloseConvertToSubAccount}>
                    {t('account:status.retry_registration.dialog.button.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      updateAccountStatus.mutate(
                        {
                          account_id: account?.id,
                          type: AccountType.WP_SUB_ACCOUNT,
                        },
                        {
                          onError: (error: any) => {
                            onCloseConvertToSubAccount();
                            manageErrorResponse(toast, error.response);
                          },
                          onSuccess: () => {
                            onCloseConvertToSubAccount();
                          },
                        },
                      );
                    }}
                  >
                    {t('account:status.marked_as_buttons.convert_to_sub_account.label')}
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </>
        )}
      </HStack>

      <HStack mt={5}>
        {(accountStatusOnHoldAndNoOpenTask || (portalRegistrationNeeded && accountStatusLessThenRegistered)) && (
          <Button
            variant="primary"
            disabled={!canMarkAsRegistered}
            onClick={() => {
              if (needsLegalForm) {
                onOpenSetLegalForm();
                return;
              }
              markAsRegistered();
            }}
          >
            {!canMarkAsRegistered ? (
              <Tooltip
                label={t(
                  hasNoUserNameOrPassword
                    ? 'account:status.registration_completed.tooltip'
                    : 'account:status.registration_completed.tooltip_totp_required',
                )}
              >
                {t('account:status.registration_completed.label')}
              </Tooltip>
            ) : (
              t('account:status.registration_completed.label')
            )}
          </Button>
        )}
        {accountStatusOnHoldAndNoOpenTask && (
          <Button
            color="red.600"
            borderColor="red.600"
            variant="outline"
            _hover={{
              bg: 'red.600',
              color: 'white',
            }}
            _active={{
              bg: 'red.600',
              color: 'white',
            }}
            onClick={() => {
              updateAccountStatus.mutate({
                account_id: account?.id,
                status: 800,
                portal_id: account?.portal?.id,
                workshop_id: account?.workshop_id,
                username: account?.username,
                password: account?.password,
                random_password: false,
              });
            }}
          >
            {t('account:status.registration_rejected.rejected')}
          </Button>
        )}
      </HStack>

      {isVagDssPortal && (
        <SetLegalFormModal
          workshopId={account?.workshop_id}
          accountId={account?.id}
          isOpen={isOpenSetLegalForm}
          onClose={onCloseSetLegalForm}
          onSuccess={() => {
            onCloseSetLegalForm();
            markAsRegistered();
          }}
        />
      )}
    </div>
  );
};
